<script>
    import { slide } from "svelte/transition";
    import { createEventDispatcher } from "svelte";
    import { get } from "svelte/store";
    import {
        supplyPlanSearch,
        appliedSupplyPlanSearch,
        planWeeks,
        productTypes,
        itemGroups,
        modelIntegrations,
        models,
        ALL_WEEKS,
        defaultWeekRange,
        exportToCSV,
        filteredSupplyPlan,
        fetchSupplyPlanFromDB,
        isDataLoading,
        dataStatus,
    } from "./store.js";
    import {
        getPresetDateRanges,
        getWeekRangeInfo,
        formatDate,
        getWeekFromDate,
    } from "./dateWeekUtil.js";
    import toast from "svelte-french-toast";
    import MondayDatePicker from "../../components/MondayDatePicker.svelte";

    export let openTreeModal;
    export let openColumnModal;

    const dispatch = createEventDispatcher();

    // 빠른선택 프리셋 목록
    const presets = getPresetDateRanges();

    // 현재 선택된 날짜에 대한 실시간 주차 정보
    $: currentRangeInfo = getWeekRangeInfo($supplyPlanSearch.startDate, $supplyPlanSearch.endDate);

    // 선택된 프리셋 상태 ('default' | 'fullYear' | 'h1' | 'h2' | null)
    let selectedPreset = "default";

    // 날짜 일치 여부 판정
    $: isDefaultDates = $supplyPlanSearch.startDate === $defaultWeekRange.startDate && $supplyPlanSearch.endDate === $defaultWeekRange.endDate;
    $: isFullYearDates = $supplyPlanSearch.startDate === presets.fullYear.startDate && $supplyPlanSearch.endDate === presets.fullYear.endDate;
    $: isH1Dates = $supplyPlanSearch.startDate === presets.h1.startDate && $supplyPlanSearch.endDate === presets.h1.endDate;
    $: isH2Dates = $supplyPlanSearch.startDate === presets.h2.startDate && $supplyPlanSearch.endDate === presets.h2.endDate;

    // 만약 기본값과 전체의 날짜가 동일하더라도 selectedPreset으로 명확히 구분
    $: isDefaultActive = selectedPreset === "default" || (!selectedPreset && isDefaultDates);
    $: isFullYearActive = selectedPreset === "fullYear" || (!selectedPreset && !isDefaultDates && isFullYearDates);
    $: isH1Active = selectedPreset === "h1" || (!selectedPreset && isH1Dates);
    $: isH2Active = selectedPreset === "h2" || (!selectedPreset && isH2Dates);

    // $defaultWeekRange가 바뀌고 현재 검색일자가 기본값과 일치하면 'default' 프리셋으로 동기화
    let prevDefaultKey = "";
    $: currentDefaultKey = `${$defaultWeekRange.startDate}_${$defaultWeekRange.endDate}`;
    $: if (currentDefaultKey !== prevDefaultKey) {
        prevDefaultKey = currentDefaultKey;
        if (isDefaultDates) {
            selectedPreset = "default";
        }
    }

    function handleStartDateChange(val) {
        selectedPreset = null;
        if (!val) return;
        supplyPlanSearch.update(s => {
            let end = s.endDate;
            if (end && end < val) {
                end = val;
            }
            const info = getWeekRangeInfo(val, end);
            return {
                ...s,
                startDate: val,
                endDate: end,
                startYear: info ? info.startYear : s.startYear,
                endYear: info ? info.endYear : s.endYear,
                startWeek: info ? info.startWeek : s.startWeek,
                endWeek: info ? info.endWeek : s.endWeek,
            };
        });
    }

    function handleEndDateChange(val) {
        selectedPreset = null;
        if (!val) return;
        supplyPlanSearch.update(s => {
            let start = s.startDate;
            if (start && start > val) {
                start = val;
            }
            const info = getWeekRangeInfo(start, val);
            return {
                ...s,
                startDate: start,
                endDate: val,
                startYear: info ? info.startYear : s.startYear,
                endYear: info ? info.endYear : s.endYear,
                startWeek: info ? info.startWeek : s.startWeek,
                endWeek: info ? info.endWeek : s.endWeek,
            };
        });
    }

    function applyPreset(type) {
        selectedPreset = type;
        let target = presets.default;
        if (type === "default") {
            target = $defaultWeekRange;
        } else if (type === "fullYear") {
            target = presets.fullYear;
        } else if (type === "h1") {
            target = presets.h1;
        } else if (type === "h2") {
            target = presets.h2;
        }

        const info = getWeekRangeInfo(target.startDate, target.endDate);
        supplyPlanSearch.update(s => ({
            ...s,
            startDate: target.startDate,
            endDate: target.endDate,
            startYear: info ? info.startYear : s.startYear,
            endYear: info ? info.endYear : s.endYear,
            startWeek: info ? info.startWeek : s.startWeek,
            endWeek: info ? info.endWeek : s.endWeek,
        }));
    }

    function toggleMonthSum() {
        supplyPlanSearch.update(s => ({
            ...s,
            monthSum: !s.monthSum,
        }));
    }

    // 제품유형 select 실시간 변경 핸들러
    function handleProductTypeChange(e) {
        const val = e.target.value;
        supplyPlanSearch.update(s => {
            let newGrp = s.itemGroup;
            if (val && val !== "ALL") {
                const validGrps = ($itemGroups || []).filter(ig => ig.mlsfc_code === val);
                if (!validGrps.some(ig => ig.item_group_code === newGrp)) {
                    newGrp = "";
                }
            }
            return {
                ...s,
                productType: val,
                itemGroup: newGrp,
                selectedProductTypes: val ? [val] : [],
                selectedItemGroups: newGrp ? [newGrp] : [],
                selectedGroupModels: [],
                selectedTreeIds: [],
            };
        });
    }

    // 품목그룹 select 실시간 변경 핸들러
    function handleItemGroupChange(e) {
        const val = e.target.value;
        supplyPlanSearch.update(s => {
            let newPt = s.productType;
            if (val && val !== "ALL") {
                const matched = ($itemGroups || []).find(ig => ig.item_group_code === val);
                if (matched && matched.mlsfc_code) {
                    newPt = matched.mlsfc_code;
                }
            }
            return {
                ...s,
                productType: newPt,
                itemGroup: val,
                selectedProductTypes: newPt ? [newPt] : [],
                selectedItemGroups: val ? [val] : [],
                selectedGroupModels: [],
                selectedTreeIds: [],
            };
        });
    }

    // 현재 UI 조건과 실제 테이블에 적용된 조건이 다른지 비교 (조회버튼 유도)
    function areConditionsDifferent(a, b) {
        if (!a || !b) return false;
        const keys = [
            "startDate", "endDate", "baseWeek", "compareWeek", "isMultiWeek", "excludeZero", "monthSum",
            "itemKeyword", "productType", "itemGroup",
            "modelIntegration", "model", "region", "team", "customer"
        ];
        return keys.some(k => a[k] !== b[k]);
    }

    $: isConditionChanged = areConditionsDifferent($supplyPlanSearch, $appliedSupplyPlanSearch);

    // 제품유형 선택 시 품목그룹 필터링
    $: filteredItemGroups = ($supplyPlanSearch.productType && $supplyPlanSearch.productType !== "ALL")
        ? $itemGroups.filter(ig => ig.mlsfc_code === $supplyPlanSearch.productType)
        : $itemGroups;

    // 통합기종 선택 시 기종 필터링
    $: filteredModels = ($supplyPlanSearch.modelIntegration && $supplyPlanSearch.modelIntegration !== "ALL")
        ? $models.filter(m => m.mhrls_unity_code === $supplyPlanSearch.modelIntegration)
        : $models;

    // 기준주차 목록 (전체 노출)
    $: filteredPlanWeeks = $planWeeks || [];

    async function handleSearch() {
        await fetchSupplyPlanFromDB(get(supplyPlanSearch));
        dispatch("search");
        toast.success("공급계획(PSI) 데이터를 조회했습니다.", { duration: 1500 });
    }

    function handleReset() {
        const defaultBase = $planWeeks && $planWeeks.length > 0 ? $planWeeks[0].code : "";
        const defaultCompare = $planWeeks && $planWeeks.length > 1 ? $planWeeks[1].code : defaultBase;
        const weekRange = $defaultWeekRange;
        supplyPlanSearch.set({
            excludeZero: true,
            isMultiWeek: false,
            monthSum: false,
            startDate: weekRange.startDate,
            endDate: weekRange.endDate,
            startYear: weekRange.startYear,
            endYear: weekRange.endYear,
            startWeek: weekRange.startWeek,
            endWeek: weekRange.endWeek,
            baseWeek: defaultBase,
            compareWeek: defaultCompare,
            itemKeyword: "",
            productType: "",
            itemGroup: "",
            modelIntegration: "",
            model: "",
            category: "ALL",
            itemCode: "",
            region: "ALL",
            team: "ALL",
            customer: "",
            isExpanded: true,
            selectedItemGroups: [],
            selectedProductTypes: [],
            selectedModels: [],
            selectedGroupModels: [],
            selectedTreeIds: [],
        });
        toast("조회 조건이 초기화되었습니다.", { icon: "🔄", duration: 1500 });
    }

    function handleBaseWeekChange(e) {
        const val = e.target.value;
        supplyPlanSearch.update(s => ({
            ...s,
            baseWeek: val,
        }));
    }

    function handleCompareWeekChange(e) {
        const val = e.target.value;
        supplyPlanSearch.update(s => ({
            ...s,
            compareWeek: val,
        }));
    }

    function handleExportCSV() {
        try {
            if (isConditionChanged) {
                toast("조회 조건이 변경되었지만 '조회하기'를 누르지 않았습니다. 마지막으로 조회한 조건으로 다운로드됩니다.", { icon: "⚠️", duration: 4000 });
            }
            exportToCSV($filteredSupplyPlan);
            toast.success("공급계획 CSV 파일이 다운로드되었습니다.", { duration: 2000 });
        } catch (e) {
            console.error(e);
            toast.error("다운로드 중 오류가 발생했습니다.");
        }
    }
</script>

<div class="search-area">
    {#if $supplyPlanSearch.isExpanded}
        <section class="search-panel">
            <!-- 1행: 편성주차(기준/비교), 조회구간(달력 시작~종료), 옵션(체크박스그룹) -->
            <div class="search-grid search-grid-row1">
                <div class="field">
                    <label>기준주차</label>
                    <select
                        value={$supplyPlanSearch.baseWeek}
                        on:change={handleBaseWeekChange}
                    >
                        {#if filteredPlanWeeks.length === 0}
                            <option value="">주차 목록 없음</option>
                        {/if}
                        {#each filteredPlanWeeks as pw}
                            <option value={pw.code}>{pw.name}</option>
                        {/each}
                    </select>
                </div>

                <div class="field">
                    <label>비교주차</label>
                    <select
                        value={$supplyPlanSearch.compareWeek}
                        on:change={handleCompareWeekChange}
                        disabled={!$supplyPlanSearch.isMultiWeek}
                    >
                        {#if filteredPlanWeeks.length === 0}
                            <option value="">주차 목록 없음</option>
                        {/if}
                        {#each filteredPlanWeeks as pw}
                            <option value={pw.code}>{pw.name}</option>
                        {/each}
                    </select>
                </div>

                <div class="field date-range-field">
                    <div class="field-title-row">
                        <label>조회구간</label>
                        <div class="range-presets">
                            <button
                                type="button"
                                class="preset-btn"
                                class:active={isDefaultActive}
                                on:click={() => applyPreset("default")}
                                title={$defaultWeekRange.isCustom
                                    ? `컬럼설정 기본 조회구간 (${$defaultWeekRange.label})`
                                    : `기본 조회구간 (${$defaultWeekRange.label})`}
                            >기본({$defaultWeekRange.totalWeeks}주)</button>
                            <button
                                type="button"
                                class="preset-btn"
                                class:active={isFullYearActive}
                                on:click={() => applyPreset("fullYear")}
                                title="당해 연도 전체"
                            >전체</button>
                            <button
                                type="button"
                                class="preset-btn"
                                class:active={isH1Active}
                                on:click={() => applyPreset("h1")}
                                title="상반기 (1~6월)"
                            >상반기</button>
                            <button
                                type="button"
                                class="preset-btn"
                                class:active={isH2Active}
                                on:click={() => applyPreset("h2")}
                                title="하반기 (7~12월)"
                            >하반기</button>
                            <button
                                type="button"
                                class="preset-btn month-sum-btn"
                                class:active={$supplyPlanSearch.monthSum}
                                on:click={toggleMonthSum}
                                title={$supplyPlanSearch.monthSum
                                    ? "현재 월별 보기 (클릭 시 주별로 전환)"
                                    : "현재 주별 보기 (클릭 시 현재 조회구간을 월별 합계로 전환)"}
                            >{$supplyPlanSearch.monthSum ? "월별" : "주별"}</button>
                        </div>
                    </div>
                    <div class="date-range-inputs">
                        <div class="date-input-wrap">
                            <MondayDatePicker
                                value={$supplyPlanSearch.startDate}
                                on:change={(e) => handleStartDateChange(e.detail)}
                            />
                        </div>
                        <span class="range-sep">~</span>
                        <div class="date-input-wrap">
                            <MondayDatePicker
                                value={$supplyPlanSearch.endDate}
                                on:change={(e) => handleEndDateChange(e.detail)}
                            />
                        </div>
                    </div>
                </div>

                <div class="field checkbox-field-container">
                    <label class="invisible-label">&nbsp;</label>
                    <div class="checkbox-group">
                        <label class="checkbox-label" class:checked={$supplyPlanSearch.isMultiWeek}>
                            <input
                                type="checkbox"
                                bind:checked={$supplyPlanSearch.isMultiWeek}
                            />
                            <span>복수주차비교</span>
                        </label>
                        <label class="checkbox-label" class:checked={$supplyPlanSearch.excludeZero}>
                            <input
                                type="checkbox"
                                bind:checked={$supplyPlanSearch.excludeZero}
                            />
                            <span>0 제외</span>
                        </label>
                    </div>
                </div>
            </div>

            <!-- 2행: 품목조회, 제품유형, 품목그룹, 통합기종, 기종, 도구버튼 -->
            <div class="search-grid search-grid-row2">
                <div class="field">
                    <label>품목조회</label>
                    <input
                        type="text"
                        placeholder="품목코드 또는 품명 입력"
                        bind:value={$supplyPlanSearch.itemKeyword}
                        on:keydown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                </div>

                <div class="field">
                    <label>제품유형</label>
                    <select value={$supplyPlanSearch.productType} on:change={handleProductTypeChange}>
                        <option value="">전체 (ALL)</option>
                        {#each $productTypes as pt}
                            <option value={pt.mlsfc_code}>{pt.mlsfc_nm}</option>
                        {/each}
                    </select>
                </div>

                <div class="field">
                    <label>품목그룹</label>
                    <select value={$supplyPlanSearch.itemGroup} on:change={handleItemGroupChange}>
                        <option value="">전체 (ALL)</option>
                        {#each filteredItemGroups as ig}
                            <option value={ig.item_group_code}>{ig.item_group_nm || ig.item_grup_nm}</option>
                        {/each}
                    </select>
                </div>

                <div class="field">
                    <label>통합기종</label>
                    <select bind:value={$supplyPlanSearch.modelIntegration}>
                        <option value="">전체 (ALL)</option>
                        {#each $modelIntegrations as mi}
                            <option value={mi.refrn_code}>{mi.refrn_nm}</option>
                        {/each}
                    </select>
                </div>

                <div class="field">
                    <label>기종</label>
                    <select bind:value={$supplyPlanSearch.model}>
                        <option value="">전체 (ALL)</option>
                        {#each filteredModels as m}
                            <option value={m.mhrls_knd_code}>{m.mhrls_knd_nm || m.mhrls_knd_code}</option>
                        {/each}
                    </select>
                </div>

                <div class="field tool-buttons-container">
                    <label class="invisible-label">&nbsp;</label>
                    <div class="tool-btn-group">
                        <button class="tool-btn" on:click={openTreeModal} title="제품/영업 분류 트리 열기">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                                <polyline points="2 17 12 22 22 17"/>
                                <polyline points="2 12 12 17 22 12"/>
                            </svg>
                            트리선택
                        </button>
                        <button class="tool-btn" on:click={openColumnModal} title="컬럼 구성 및 표시 설정">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <line x1="4" y1="21" x2="4" y2="14"/>
                                <line x1="4" y1="10" x2="4" y2="3"/>
                                <line x1="12" y1="21" x2="12" y2="12"/>
                                <line x1="12" y1="8" x2="12" y2="3"/>
                                <line x1="20" y1="21" x2="20" y2="16"/>
                                <line x1="20" y1="12" x2="20" y2="3"/>
                                <line x1="1" y1="14" x2="7" y2="14"/>
                                <line x1="9" y1="8" x2="15" y2="8"/>
                                <line x1="17" y1="16" x2="23" y2="16"/>
                            </svg>
                            컬럼설정
                        </button>
                        <button class="tool-btn excel-tool-btn" on:click={handleExportCSV} title="엑셀 CSV 다운로드">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="7 10 12 15 17 10"/>
                                <line x1="12" y1="15" x2="12" y2="3"/>
                            </svg>
                            엑셀
                        </button>
                    </div>
                </div>
            </div>

            <!-- 데이터 조회하기 메인 버튼 -->
            <button
                class="main-btn"
                class:highlight-search={isConditionChanged}
                on:click={handleSearch}
                disabled={$isDataLoading}
            >
                {#if $isDataLoading}
                    데이터 조회 중...
                {:else if isConditionChanged}
                    🔍 조건 변경됨 — 클릭하여 조회하기
                {:else}
                    🔍 조회하기
                {/if}
            </button>
        </section>
    {/if}

    <!-- 조회 조건 접기/펼치기 토글 바 (기존 Shipment와 100% 동일) -->
    <div
        class="toggle-bar"
        on:click={() =>
            supplyPlanSearch.update((s) => ({
                ...s,
                isExpanded: !s.isExpanded,
            }))}
    >
        <div class="toggle-content">
            <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
            >
                <path
                    d={$supplyPlanSearch.isExpanded
                        ? "m18 15-6-6-6 6"
                        : "m6 9 6 6 6-6"}
                />
            </svg>
            <span>
                {$supplyPlanSearch.isExpanded
                    ? "조회 조건 접기"
                    : "조회 조건 펼쳐서 다시 검색하기"}
            </span>
        </div>
    </div>
</div>

<style>
    .search-area {
        flex-shrink: 0;
        background: white;
        border-bottom: 1px solid #e2e8f0;
        padding: 15px 15px 0 15px;
    }
    .search-panel {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding-bottom: 10px;
    }
    .search-grid {
        display: grid;
        gap: 10px;
    }
    .search-grid-row1 {
        grid-template-columns: 1fr 1fr 2.4fr 1.05fr;
    }
    .date-input-wrap {
        flex: 1;
        min-width: 0;
        position: relative;
    }
    .search-grid-row2 {
        grid-template-columns: 1.2fr 1fr 1fr 1fr 1fr 1.05fr;
    }
    .field > label {
        display: block;
        font-size: 0.7rem;
        font-weight: 800;
        color: #64748b;
        margin-bottom: 4px;
    }
    .field input:not([type="checkbox"]),
    .field select {
        width: 100%;
        height: 35px;
        border: 1.5px solid #e2e8f0;
        border-radius: 4px;
        padding: 0 8px;
        font-size: 0.85rem;
        box-sizing: border-box;
        background-color: #fff;
        color: #1e293b;
    }
    .field input:focus,
    .field select:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
    .field select:disabled,
    .field input:disabled {
        background-color: #f8fafc;
        color: #94a3b8;
        cursor: not-allowed;
    }
    .field-title-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 4px;
    }
    .field-title-row > label {
        font-size: 0.7rem;
        font-weight: 800;
        color: #64748b;
        margin-bottom: 0;
        display: inline-flex;
        align-items: center;
        gap: 5px;
    }
    .range-presets {
        display: flex;
        align-items: center;
        gap: 4px;
    }
    .preset-btn {
        background: #f1f5f9;
        border: 1px solid #cbd5e1;
        border-radius: 4px;
        padding: 2px 7px;
        font-size: 0.7rem;
        font-weight: 700;
        color: #475569;
        cursor: pointer;
        line-height: 1.25;
        transition: all 0.15s ease;
        outline: none;
        user-select: none;
    }
    .preset-btn:disabled {
        background: #e9ecef;
        color: #adb5bd;
        border-color: #dee2e6;
        cursor: not-allowed;
        pointer-events: none;
    }
    /* 비활성 상태 hover */
    .preset-btn:not(.active):not(:disabled):hover {
        background: #e2e8f0;
        color: #1e293b;
        border-color: #94a3b8;
    }
    /* 활성(Active) 상태: 클릭 후 hover되어도 선명한 파란색 배경과 흰색 텍스트가 절대 풀리지 않음 */
    .preset-btn.active,
    .preset-btn.active:hover,
    .preset-btn.active:focus {
        background: #2563eb !important;
        color: #ffffff !important;
        border-color: #1d4ed8 !important;
        font-weight: 800 !important;
        box-shadow: 0 1px 3px rgba(37, 99, 235, 0.4);
    }
    .preset-btn.active:hover {
        background: #1d4ed8 !important;
    }

    /* 월 합계 버튼 기본 및 활성 상태 */
    .preset-btn.month-sum-btn {
        background: #fffbeb;
        border-color: #fcd34d;
        color: #92400e;
    }
    .preset-btn.month-sum-btn:not(.active):hover {
        background: #fef3c7;
        border-color: #f59e0b;
        color: #78350f;
    }
    .preset-btn.month-sum-btn.active,
    .preset-btn.month-sum-btn.active:hover,
    .preset-btn.month-sum-btn.active:focus {
        background: #f59e0b !important;
        border-color: #d97706 !important;
        color: #ffffff !important;
        font-weight: 800 !important;
        box-shadow: 0 1px 3px rgba(245, 158, 11, 0.4);
    }
    .preset-btn.month-sum-btn.active:hover {
        background: #d97706 !important;
    }
    .date-range-inputs {
        display: flex;
        align-items: center;
        gap: 3px;
    }
    .range-sep {
        font-size: 0.75rem;
        color: #94a3b8;
        font-weight: 700;
        padding: 0 1px;
    }
    .invisible-label {
        visibility: hidden;
    }
    .checkbox-group {
        display: flex;
        align-items: center;
        width: 100%;
        height: 35px;
        border: 1.5px solid #e2e8f0;
        border-radius: 4px;
        background: #ffffff;
        box-sizing: border-box;
        overflow: hidden;
    }
    .checkbox-label {
        display: flex !important;
        align-items: center;
        justify-content: center;
        font-size: 0.82rem;
        font-weight: 700;
        color: #475569;
        cursor: pointer;
        user-select: none;
        flex: 1;
        height: 100%;
        margin: 0 !important;
        padding: 0 4px;
        background: #ffffff;
        transition: all 0.2s ease;
        position: relative;
    }
    .checkbox-label span {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
    }
    .checkbox-label:not(:last-child) {
        border-right: 1.5px solid #e2e8f0;
    }
    .checkbox-label:not(.checked):hover {
        background: #f8fafc;
        color: #1e293b;
    }
    .checkbox-label.checked {
        background: #3b82f6;
        color: #ffffff;
    }
    .checkbox-label input[type="checkbox"] {
        position: absolute;
        opacity: 0;
        cursor: pointer;
        height: 0;
        width: 0;
    }

    /* 보조 툴 버튼 그룹 */
    .tool-btn-group {
        display: flex;
        align-items: center;
        gap: 6px;
        height: 35px;
    }
    .tool-btn {
        flex: 1;
        height: 35px;
        border: 1.5px solid #e2e8f0;
        border-radius: 4px;
        background: #ffffff;
        color: #475569;
        font-size: 0.8rem;
        font-weight: 700;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        transition: all 0.2s;
        padding: 0 6px;
        white-space: nowrap;
    }
    .tool-btn:hover {
        background: #f8fafc;
        border-color: #cbd5e1;
        color: #1e293b;
    }
    .excel-tool-btn {
        color: #059669;
        border-color: #a7f3d0;
        background: #f0fdf4;
    }
    .excel-tool-btn:hover {
        background: #dcfce7;
        border-color: #86efac;
    }

    /* 메인 버튼 */
    .main-btn {
        width: 100%;
        height: 40px;
        background: #3b82f6;
        color: white;
        border: none;
        border-radius: 4px;
        font-weight: 800;
        margin-top: 10px;
        cursor: pointer;
        font-size: 0.95rem;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
    }
    .main-btn:hover {
        background: #2563eb;
    }
    .main-btn.highlight-search {
        background: #1d4ed8;
        box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.35);
        animation: pulseSearch 1.8s infinite;
    }
    .main-btn.highlight-search:hover {
        background: #1e40af;
    }
    @keyframes pulseSearch {
        0%, 100% {
            box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.4);
        }
        50% {
            box-shadow: 0 0 0 5px rgba(37, 99, 235, 0.15);
        }
    }

    /* 토글 바 */
    .toggle-bar {
        padding: 8px 0;
        background: #ffffff;
        cursor: pointer;
        display: flex;
        justify-content: center;
        border-top: 1px solid #f1f5f9;
    }
    .toggle-content {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #3b82f6;
        font-size: 0.85rem;
        font-weight: 800;
    }

    @media (max-width: 1100px) {
        .search-grid-row1 {
            grid-template-columns: repeat(3, 1fr);
        }
        .search-grid-row2 {
            grid-template-columns: repeat(2, 1fr);
        }
    }
    @media (max-width: 768px) {
        .search-grid-row1,
        .search-grid-row2 {
            grid-template-columns: repeat(2, 1fr);
        }
    }
</style>
